// <copyright file="token-refresh.service.ts" company="BD">
// Copyright (c) BD. All rights reserved.
// </copyright>

import { inject, Injectable, OnDestroy } from "@angular/core";
import { EMPTY, Subject, Subscription, timer } from "rxjs";
import { catchError, finalize } from "rxjs/operators";
import { AuthTokenStore } from "../../shared/stores/auth-token-store";
import { MobileSoftwareUpgradeApiService } from "../../core/http/apiservice";
import { mobileSoftwareUpgradeEndPoint } from "../../../assets/data/softwareupgradeconstant";
import { TOKEN_REFRESH_CONFIG, getRefreshDelayMs } from "./token-refresh.config";
import { SilentRefreshResponse } from "./token-refresh-api-response-model";

/**
 * Token Refresh Service
 *
 * Handles automatic silent refresh of authentication tokens.
 * - Schedules refresh at configurable time (default: 14th minute)
 * - The silent API extends the existing token's validity (no new token issued)
 * - Logs errors to console for monitoring
 */
@Injectable({
  providedIn: "root"
})
export class TokenRefreshService implements OnDestroy {
  private readonly authTokenStore = inject(AuthTokenStore);
  private readonly apiService = inject(MobileSoftwareUpgradeApiService);

  private destroy$ = new Subject<void>();
  private refreshTimerSubscription: Subscription | null = null;
  private isRefreshing = false;

  /**
   * Start the token refresh timer
   * Should be called after token is set during initialization
   */
  startRefreshTimer(): void {
    if (!TOKEN_REFRESH_CONFIG.enableRefresh) {
      return;
    }
    // Clear any existing timer
    this.stopRefreshTimer();

    const token = this.authTokenStore.getAccessToken();
    if (!token) {
      return;
    }

    const refreshDelayMs = getRefreshDelayMs();

    console.log("[TokenRefresh] Starting refresh timer");
    // Schedule the refresh using RxJS timer
    this.refreshTimerSubscription = timer(refreshDelayMs).subscribe(() => {
      this.executeSilentRefresh();
    });
  }

  /**
   * Stop the refresh timer
   */
  stopRefreshTimer(): void {
    if (this.refreshTimerSubscription) {
      this.refreshTimerSubscription.unsubscribe();
      console.log("[TokenRefresh] Refresh timer stopped");
      this.refreshTimerSubscription = null;
    }
  }

  /**
   * Execute the silent refresh API call
   * Calls the silent API to extend the existing token's validity
   */
  private executeSilentRefresh(): void {
    if (this.isRefreshing) {
      return;
    }

    const currentToken = this.authTokenStore.getAccessToken();
    if (!currentToken) {
      return;
    }

    this.isRefreshing = true;

    this.apiService
      .get<SilentRefreshResponse>(mobileSoftwareUpgradeEndPoint.userManagementAPI.silentLogin)
      .pipe(
        catchError((error) => {
          this.handleRefreshFailure(error);
          return EMPTY;
        }),
        finalize(() => {
          this.isRefreshing = false;
        })
      )
      .subscribe((response) => {
        this.handleRefreshSuccess(response);
      });
  }

  /**
   * Handle successful token refresh
   * The silent API extends the existing token - no new token is issued
   */
  private handleRefreshSuccess(response: SilentRefreshResponse): void {
    if (response?.status !== "Success") {
      console.error("[TokenRefresh] Silent refresh failed");
      return;
    }

    // Schedule the next refresh
    this.startRefreshTimer();
  }

  /**
   * Handle refresh failure
   */
  private handleRefreshFailure(error: unknown): void {
    // Log additional details for debugging
    if (error instanceof Error) {
      console.error("[TokenRefresh] Error details:", {
        message: error.message
      });
      this.stopRefreshTimer();
    }
  }

  /**
   * Cleanup on service destruction
   */
  ngOnDestroy(): void {
    this.stopRefreshTimer();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
